'use client';

import React from 'react';
import { format } from 'date-fns';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';
import { StrapiImage } from '../ui/strapi-image';

import { cn } from '@/lib/utils';

export const SingleArticle = ({
  heading,
  article,
  locale,
}: {
  heading?: string;
  article: any;
  locale: string;
}) => {
  const articleData = article?.data || article;

  if (!articleData || !articleData.slug) return null;

  return (
    <div className="relative z-20 overflow-hidden">
      <article className="w-full">
        <div className="relative">
          {articleData.image && (
            <div className="relative h-[30vh] md:h-[40vh] w-full">
              <StrapiImage
                src={articleData.image.url}
                alt={articleData.title}
                fill
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            </div>
          )}
          <div
            className={cn(
              'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8',
              articleData.image
                ? 'absolute bottom-0 left-1/2 -translate-x-1/2 w-full z-10 pt-32 pb-12 md:pt-40 md:pb-20'
                : 'relative py-12 md:py-20'
            )}
          >
            <div className="flex gap-2 flex-wrap mb-4 mt-8">
              {articleData.categories?.map((category: any, idx: number) => (
                <span
                  key={`category-${idx}`}
                  className={cn(
                    'text-[10px] uppercase font-bold tracking-widest px-3 py-1 rounded-full',
                    articleData.image
                      ? 'bg-white/20 text-white backdrop-blur-md border border-white/10'
                      : 'bg-primary/10 text-primary dark:bg-primary/20'
                  )}
                >
                  {category.name}
                </span>
              ))}
            </div>
            <h2
              className={cn(
                'text-4xl md:text-6xl font-extrabold mb-4 leading-tight',
                articleData.image
                  ? 'text-white drop-shadow-lg'
                  : 'text-neutral-900 dark:text-white'
              )}
            >
              {articleData.title}
            </h2>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div
            className="prose prose-xl dark:prose-invert max-w-none 
            prose-headings:font-bold prose-headings:tracking-tight
            prose-a:text-primary hover:prose-a:text-primary/80 prose-a:transition-colors
            prose-img:rounded-3xl prose-img:shadow-2xl
            prose-p:leading-relaxed prose-p:text-neutral-700 dark:prose-p:text-neutral-300
            prose-strong:text-neutral-900 dark:prose-strong:text-white"
          >
            {articleData.content && (
              <BlocksRenderer content={articleData.content} />
            )}
          </div>
        </div>
      </article>
    </div>
  );
};
